import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { deleteMerchantRules } from 'Services';
import { RQ_HISTORY, RQ_MERCHANT_RULES, RQ_RULE_REVISION_MERCHANT_OVERRIDES } from 'Utils';
import useSingleMerchantErrorHandler from './useSingleMerchantErrorHandler';

const deleteOverride = (templateRepositoryId: number, merchantId: string) => (ruleIds: string[]) => {
    return deleteMerchantRules(templateRepositoryId, merchantId, {
        ruleIds
    });
};

type MutationOptions = UseMutationOptions<AxiosResponse<any>, unknown, string[], unknown>;

type Arguments = {
    mid: string;
    revision: number;
    mutationOptions: MutationOptions;
    ruleId?: string;
};

function useDeleteMerchantRule({ ruleId, mid, mutationOptions: { onSuccess, onError } }: Arguments) {
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const queryClient = useQueryClient();
    const errorHandler = useSingleMerchantErrorHandler({
        mutationOptions: {
            onError
        },
        mode: 'deleteMerchantRule',
        ruleId,
        queryInvalidateFunction: () => {
            queryClient.invalidateQueries([RQ_MERCHANT_RULES, templateRepositoryId, mid]);
            queryClient.invalidateQueries([RQ_HISTORY]);
        },
        mid
    });

    return useMutation(deleteOverride(templateRepositoryId!, mid), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries([RQ_MERCHANT_RULES, templateRepositoryId, mid]);
            queryClient.invalidateQueries([RQ_HISTORY]);
            queryClient.invalidateQueries([RQ_RULE_REVISION_MERCHANT_OVERRIDES]);
            onSuccess?.(data, variables, context);
        },
        onError: errorHandler
    });
}

export default useDeleteMerchantRule;
